import { useEffect, useState } from "react";
import axios from "axios";
import { Download, LogOut, RefreshCw, Search, Users, AlertCircle, Lock } from "lucide-react";
import Logo from "@/components/landing/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "truopay_admin_basic_auth";

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
};

const intentColors = {
    book_demo: "bg-emerald-100 text-emerald-800",
    sign_up: "bg-amber-100 text-amber-800",
    login: "bg-sky-100 text-sky-800",
    contact: "bg-zinc-100 text-zinc-700",
    contact_request: "bg-fuchsia-100 text-fuchsia-800",
    newsletter: "bg-violet-100 text-violet-700",
};

function LoginScreen({ onAuth }) {
    const [user, setUser] = useState("");
    const [pwd, setPwd] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        const token = btoa(`${user}:${pwd}`);
        try {
            const r = await axios.get(`${API}/admin/stats`, {
                headers: { Authorization: `Basic ${token}` },
            });
            if (r.status === 200) {
                sessionStorage.setItem(TOKEN_KEY, token);
                onAuth(token);
            }
        } catch (e2) {
            setErr(e2?.response?.status === 401 ? "Invalid credentials" : "Network error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid place-items-center bg-mesh-emerald text-white px-4">
            <div className="w-full max-w-md rounded-3xl border border-white/10 bg-zinc-950/80 backdrop-blur-xl p-8 shadow-2xl">
                <Logo variant="dark" />
                <div className="mt-6">
                    <span className="inline-flex items-center gap-2 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-400/30 px-2.5 py-1 text-[10px] tracking-[0.25em] uppercase font-bold">
                        <Lock className="h-3 w-3" /> Internal
                    </span>
                    <h1 className="font-heading mt-3 text-3xl font-extrabold tracking-tight">
                        Admin sign-in
                    </h1>
                    <p className="text-sm text-zinc-400 mt-1">
                        HTTP Basic auth. Credentials are configured via backend env.
                    </p>
                </div>

                <form onSubmit={submit} className="mt-8 space-y-4" noValidate>
                    <label className="block">
                        <span className="text-xs font-bold tracking-widest uppercase text-zinc-400">
                            Username
                        </span>
                        <input
                            type="text"
                            data-testid="admin-username"
                            value={user}
                            onChange={(e) => setUser(e.target.value)}
                            autoComplete="username"
                            className="mt-1.5 h-12 w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 text-base text-white focus:border-emerald-500 focus:outline-none"
                        />
                    </label>
                    <label className="block">
                        <span className="text-xs font-bold tracking-widest uppercase text-zinc-400">
                            Password
                        </span>
                        <input
                            type="password"
                            data-testid="admin-password"
                            value={pwd}
                            onChange={(e) => setPwd(e.target.value)}
                            autoComplete="current-password"
                            className="mt-1.5 h-12 w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 text-base text-white focus:border-emerald-500 focus:outline-none"
                        />
                    </label>
                    {err && (
                        <div
                            data-testid="admin-login-error"
                            className="flex items-center gap-2 text-sm text-red-400"
                        >
                            <AlertCircle className="h-4 w-4" /> {err}
                        </div>
                    )}
                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="admin-login-submit"
                        className="w-full inline-flex h-12 items-center justify-center rounded-full bg-gold px-6 text-base font-bold text-zinc-950 glow-gold disabled:opacity-60"
                    >
                        {loading ? "Signing in…" : "Sign in"}
                    </button>
                </form>
            </div>
        </div>
    );
}

export default function Admin() {
    const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
    const [leads, setLeads] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");
    const [q, setQ] = useState("");
    const [intentFilter, setIntentFilter] = useState("all");

    const auth = token ? { Authorization: `Basic ${token}` } : {};

    useEffect(() => {
        document.title = "TruoPay · Admin";
    }, []);

    const load = async () => {
        if (!token) return;
        setLoading(true);
        setErr("");
        try {
            const [a, b] = await Promise.all([
                axios.get(`${API}/admin/leads`, { headers: auth }),
                axios.get(`${API}/admin/stats`, { headers: auth }),
            ]);
            setLeads(a.data || []);
            setStats(b.data || null);
        } catch (e) {
            if (e?.response?.status === 401) {
                sessionStorage.removeItem(TOKEN_KEY);
                setToken("");
            } else {
                setErr("Failed to load leads.");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const filtered = leads.filter((l) => {
        if (intentFilter !== "all" && (l.intent || "contact") !== intentFilter) return false;
        if (!q) return true;
        const hay = `${l.name} ${l.email} ${l.company || ""}`.toLowerCase();
        return hay.includes(q.toLowerCase());
    });

    const downloadCsv = async () => {
        try {
            const r = await axios.get(`${API}/admin/leads.csv`, {
                headers: auth,
                responseType: "blob",
            });
            const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `truopay-leads-${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch {
            setErr("CSV export failed.");
        }
    };

    const signOut = () => {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken("");
    };

    if (!token) {
        return <LoginScreen onAuth={setToken} />;
    }

    return (
        <main data-testid="admin-page" className="min-h-screen bg-zinc-50">
            <header className="bg-zinc-950 text-white">
                <div className="mx-auto max-w-7xl px-4 md:px-8 h-16 flex items-center justify-between">
                    <Logo variant="dark" />
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={load}
                            data-testid="admin-refresh-btn"
                            className="inline-flex h-10 items-center gap-2 rounded-full border border-white/15 px-4 text-sm font-semibold hover:bg-white/5"
                        >
                            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                            Refresh
                        </button>
                        <button
                            type="button"
                            onClick={downloadCsv}
                            data-testid="admin-csv-btn"
                            className="inline-flex h-10 items-center gap-2 rounded-full bg-gold px-4 text-sm font-bold text-zinc-950 glow-gold"
                        >
                            <Download className="h-4 w-4" />
                            CSV
                        </button>
                        <button
                            type="button"
                            onClick={signOut}
                            data-testid="admin-signout-btn"
                            className="inline-flex h-10 items-center gap-2 rounded-full border border-white/15 px-4 text-sm font-semibold hover:bg-white/5"
                        >
                            <LogOut className="h-4 w-4" /> Sign out
                        </button>
                    </div>
                </div>
            </header>

            <div className="mx-auto max-w-7xl px-4 md:px-8 py-10">
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">
                            Leads
                        </h1>
                        <p className="text-sm text-zinc-500 mt-1">
                            Captured from Book Demo, Login, Sign Up and contact flows.
                        </p>
                    </div>
                </div>

                {/* Stat cards */}
                <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                    <div className="rounded-2xl border border-zinc-200 bg-white p-5">
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-bold tracking-widest uppercase text-zinc-500">
                                Total
                            </span>
                            <Users className="h-4 w-4 text-emerald-600" />
                        </div>
                        <div className="mt-2 font-mono text-2xl font-extrabold">
                            {stats?.total ?? leads.length}
                        </div>
                    </div>
                    {["book_demo", "sign_up", "login"].map((intent) => (
                        <div key={intent} className="rounded-2xl border border-zinc-200 bg-white p-5">
                            <div className="text-xs font-bold tracking-widest uppercase text-zinc-500">
                                {intent.replace("_", " ")}
                            </div>
                            <div className="mt-2 font-mono text-2xl font-extrabold">
                                {stats?.by_intent?.[intent] ?? 0}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Filters */}
                <div className="mt-8 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                        <input
                            data-testid="admin-search"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search by name, email or company…"
                            className="h-11 w-full rounded-full border border-zinc-200 bg-white pl-10 pr-4 text-sm focus:border-emerald-500 focus:outline-none"
                        />
                    </div>
                    <select
                        data-testid="admin-intent-filter"
                        value={intentFilter}
                        onChange={(e) => setIntentFilter(e.target.value)}
                        className="h-11 rounded-full border border-zinc-200 bg-white px-4 text-sm font-semibold focus:border-emerald-500 focus:outline-none"
                    >
                        <option value="all">All intents</option>
                        <option value="contact_request">Contact request</option>
                        <option value="book_demo">Book demo</option>
                        <option value="sign_up">Sign up</option>
                        <option value="login">Login</option>
                        <option value="contact">Contact</option>
                    </select>
                </div>

                {err && (
                    <div className="mt-4 rounded-xl border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm">
                        {err}
                    </div>
                )}

                {/* Table */}
                <div className="mt-6 rounded-2xl border border-zinc-200 bg-white overflow-hidden">
                    <div className="overflow-x-auto">
                        <table data-testid="admin-leads-table" className="w-full text-sm">
                            <thead className="bg-zinc-50">
                                <tr className="text-left text-[11px] tracking-widest uppercase text-zinc-500 font-bold">
                                    <th className="px-5 py-3">Name</th>
                                    <th className="px-5 py-3">Company</th>
                                    <th className="px-5 py-3">Email</th>
                                    <th className="px-5 py-3">Phone</th>
                                    <th className="px-5 py-3">Intent</th>
                                    <th className="px-5 py-3">Source</th>
                                    <th className="px-5 py-3">Created</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-zinc-100">
                                {filtered.length === 0 && (
                                    <tr>
                                        <td colSpan={7} className="px-5 py-10 text-center text-zinc-400">
                                            {loading ? "Loading…" : "No leads match your filters yet."}
                                        </td>
                                    </tr>
                                )}
                                {filtered.map((l) => (
                                    <tr key={l.id} className="hover:bg-zinc-50 transition-colors">
                                        <td className="px-5 py-3 font-medium text-zinc-950">
                                            {l.name}
                                        </td>
                                        <td className="px-5 py-3 text-zinc-700">
                                            {l.company || "—"}
                                        </td>
                                        <td className="px-5 py-3 font-mono text-zinc-700">
                                            {l.email}
                                        </td>
                                        <td className="px-5 py-3 font-mono text-zinc-700">
                                            {l.phone || "—"}
                                        </td>
                                        <td className="px-5 py-3">
                                            <span
                                                className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-bold ${
                                                    intentColors[l.intent || "contact"] ||
                                                    "bg-zinc-100 text-zinc-700"
                                                }`}
                                            >
                                                {(l.intent || "contact").replace("_", " ")}
                                            </span>
                                        </td>
                                        <td className="px-5 py-3 text-zinc-500 font-mono text-xs">
                                            {l.source || "landing_page"}
                                        </td>
                                        <td className="px-5 py-3 text-zinc-500">
                                            {fmtDate(l.created_at)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </main>
    );
}
